import { yupResolver } from "@hookform/resolvers/yup";
import { CSpinner } from "@coreui/react";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useCookie } from "../../hooks/useCookie";
import { useToast } from "../../hooks/useToast";
import { createRewardSchema } from "../../rules/reward";
import apiService from "../../service/apiService";
import { cardStyle } from "../Places/PlacesTable";
import CreateRewardForm from "./CreateRewardForm";

interface Props {
  mode: "create" | "edit";
}

export default function AddNewReward({ mode = "create" }: Props) {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(createRewardSchema, undefined, {
      context: { mode },
    } as any),
    defaultValues: {
      promoScope: "GLOBAL",
      restrictionType: "NONE",
      event: null,
      offer: null,
      place: null,
      isFeatured: false,
    },
  });

  const navigate = useNavigate();
  const { toastError, toastSuccess } = useToast();
  const { token } = useCookie("vToken");
  const params = useParams();

  const createRewardMutation = useMutation({
    mutationFn: (data: FormData) =>
      apiService.MakePostRequest("rewards", data, token),
  });

  const updateRewardMutation = useMutation({
    mutationFn: (data: FormData) =>
      apiService.MakePutRequest(`rewards/${params.id}`, data, token),
  });

  const hasInitialized = useRef(false);

  const { data: rewardData } = useQuery({
    queryKey: ["getReward", params.id],
    queryFn: () => apiService.MakeGetRequest(`rewards/${params.id}`, token),
    enabled: mode === "edit",
    onSuccess: (data: Reward) => {
      if (hasInitialized.current) return;

      const formattedDate = data.expiresAt
        ? new Date(data.expiresAt).toISOString().split("T")[0]
        : "";

      reset({
        title: data.title,
        description: data.description,
        type: data.type,
        pointsRequired: data.pointsRequired,
        isActive: data.isActive,
        isReusable: data.isReusable,
        expiresAt: formattedDate,
        discount: data.discount,
        promoScope: data.promoScope || "GLOBAL",
        restrictionType: data.restrictionType || "NONE",
        maxUses: data.maxUses,
        event: data.event,
        offer: data.offer,
        place: data.place,
        picture: data.picture,
        isFeatured: data.isFeatured,
      } as any);

      hasInitialized.current = true;
    },
  });

  const onSubmit = async (data: any) => {
    try {
      const formData = new FormData();

      formData.append("title", data.title);
      formData.append("description", data.description || "");
      formData.append("pointsRequired", data.pointsRequired);
      formData.append("discount", data.discount || 0);
      formData.append("promoScope", data.promoScope);
      formData.append("maxUses", data.maxUses || 1);
      formData.append("expiresAt", data.expiresAt);
      formData.append("type", data.type);

      if (data.isActive !== undefined)
        formData.append("isActive", String(data.isActive));

      if (data.isReusable !== undefined)
        formData.append("isReusable", String(data.isReusable));

      if (data.isFeatured !== undefined)
        formData.append("isFeatured", String(data.isFeatured));

      if (data.restrictionType) {
        formData.append("restrictionType", data.restrictionType);
      }

      if (data.promoScope === "EVENT" && data.event?.id) {
        formData.append("eventId", data.event.id);
      }

      if (data.promoScope === "SPECIALS" && data.offer?.id) {
        formData.append("offerId", data.offer.id);
      }

      if (data.promoScope === "PLACE" && data.place?.id) {
        formData.append("placeId", data.place.id);
      }

      if (data.picture instanceof File) {
        formData.append("picture", data.picture);
      }

      if (mode === "edit") {
        await updateRewardMutation.mutateAsync(formData);
      } else {
        await createRewardMutation.mutateAsync(formData);
      }

      toastSuccess(
        mode === "edit" ? "Bonus carte mis à jour" : "Bonus carte créé",
      );
      navigate(-1);
    } catch (error) {
      toastError("Opération non réussie");
      console.error("Reward error:", error);
      return;
    }
  };

  const isLoading =
    createRewardMutation.isLoading || updateRewardMutation.isLoading;

  return (
    <div className="vz-form px-2 mt-4">
      {/* Header */}
      <div
        style={{ ...cardStyle, padding: "16px 20px" }}
        className="d-flex justify-content-between align-items-center mb-3"
      >
        <div>
          <h4 className="fs-4 fw-bold mb-0">
            {mode === "edit"
              ? "Mettre à jour le Bonus carte"
              : "Ajouter un nouveau Bonus carte"}
          </h4>
          <div className="text-muted" style={{ fontSize: 13 }}>
            Les champs marqués d'une * sont obligatoires
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="btn btn-outline-secondary rounded-pill px-3"
        >
          ← Retour
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <section style={cardStyle} className="mb-3">
          <CreateRewardForm
            control={control}
            setValue={setValue}
            watch={watch}
            errors={errors}
            data={rewardData}
            mode={mode}
          />
        </section>

        {/* Sticky actions */}
        <div
          className="d-flex justify-content-end gap-2 py-3 px-3 mt-3"
          style={{
            position: "sticky",
            bottom: 0,
            zIndex: 5,
            backgroundColor: "#fff",
            borderRadius: 16,
            boxShadow: "0 -4px 16px rgba(0,0,0,0.06)",
          }}
        >
          <button
            type="button"
            disabled={isLoading}
            onClick={() => navigate(-1)}
            className="btn btn-outline-secondary rounded-pill px-4"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-success text-white rounded-pill px-4"
          >
            {isLoading ? <CSpinner size="sm" /> : "Enregistrer"}
          </button>
        </div>
      </form>
    </div>
  );
}
